import Quill from 'quill';

/**
 * Quill 1.x has no nested-list model: a list is a flat run of <li> siblings and depth
 * is carried by a `ql-indent-N` class. HTML authored elsewhere - the Laravel rich
 * editor, which is Tiptap - nests real <ol>/<ul> inside the parent <li>.
 *
 * Quill's own clipboard matcher for that shape is broken beyond two levels: it stamps
 * the indent onto the LAST newline of the <li>'s delta, and once the <li> contains a
 * nested list that newline belongs to a descendant item. So every item that owns a
 * sub-list keeps indent 0 and its descendants shift: a list that reads
 * 1 / 1.1 / 1.1.1 in Laravel arrives here as 1 / 2 / 2.0.1.
 *
 * Rewriting the markup into Quill's own flat shape before it is traversed sidesteps
 * the matcher entirely: every <li> ends up a direct child of a top-level list with an
 * explicit `ql-indent-N`, which Quill reads through its standard IndentClass
 * attributor. Content already in the flat shape passes through untouched.
 *
 * Note this is import-only: Quill still SAVES the flat shape, so the display surfaces
 * (here and in the Laravel app) carry rules for both shapes.
 */

// Quill's IndentClass whitelist (see quill/formats/indent.js).
const MAX_INDENT = 8;

const isList = (node) => node != null && (node.tagName === 'OL' || node.tagName === 'UL');

/**
 * Depth-first walk of a list tree, detaching each nested list from its <li> and
 * recording the depth the <li> was found at.
 */
function collectItems(list, depth, items) {
  Array.from(list.children).forEach((item) => {
    if (item.tagName !== 'LI') {
      return;
    }

    const nestedLists = Array.from(item.children).filter(isList);

    nestedLists.forEach((nested) => item.removeChild(nested));
    items.push({ item, depth, tagName: list.tagName });
    nestedLists.forEach((nested) => collectItems(nested, depth + 1, items));
  });
}

/**
 * Replaces one nested list tree with the equivalent run of flat lists. Consecutive
 * items of the same kind share a container; a change of kind (a <ul> branch inside an
 * <ol>) starts a new one, which is how Quill itself renders mixed lists.
 */
function flattenList(root) {
  const items = [];

  collectItems(root, 0, items);

  const fragment = root.ownerDocument.createDocumentFragment();
  let container = null;

  items.forEach(({ item, depth, tagName }) => {
    if (container === null || container.tagName !== tagName) {
      container = root.ownerDocument.createElement(tagName);
      fragment.appendChild(container);
    }

    if (depth > 0) {
      item.classList.add(`ql-indent-${Math.min(depth, MAX_INDENT)}`);
    }

    container.appendChild(item);
  });

  root.parentNode.replaceChild(fragment, root);
}

/**
 * Flattens every nested list under `root`, in place.
 *
 * @param {HTMLElement} root
 */
export function flattenNestedLists(root) {
  Array.from(root.querySelectorAll('ol, ul'))
    .filter((list) => list.parentElement.closest('ol, ul') === null)
    .forEach(flattenList);
}

/**
 * Wraps whichever clipboard module is currently registered - the app also loads
 * quill-paste-smart, which registers its own - so both the paste path and
 * `clipboard.convert(html)`, how BaseTextEditor seeds the editor, see flat lists.
 */
export function registerNestedListClipboard() {
  const Clipboard = Quill.import('modules/clipboard');

  class NestedListClipboard extends Clipboard {
    convert(html) {
      // The string form fills `this.container` and re-enters through `this.convert()`,
      // so the flattening below still runs for it.
      if (typeof html === 'string') {
        return super.convert(html);
      }

      flattenNestedLists(this.container);

      return super.convert();
    }
  }

  Quill.register('modules/clipboard', NestedListClipboard, true);

  return NestedListClipboard;
}
